import styled from "@emotion/styled";
import redmarker from "../../assets/images/redmarker.svg";
import orangemarker from "../../assets/images/orangemarker.svg";
import SpinnerForMap from "../../components/spinnerForMap";
import theme from "../../theme";
import {
  AccidentMarker,
  BubbleTailIcon,
  CopyButton,
  DangerousDrivingMarker,
  IdleTimeMarker,
  WarningBatteryMarker,
  WarningEscapeMarker,
} from "../../assets/icons/icons";
import { UnifiedMap, UnifiedMarker, UnifiedOverlay } from "../../components/map";
import {
  copyToClipboard,
  formatDate,
} from "../../utils/util";
import { RouteInfo, EventType } from "../../types/types";
import { useMapProvider } from "../../hooks/useMapProvider";
import { useEffect, useState } from "react";
import { getAddressFromCoords } from "../../api/geocoding";
//import userStorage from "../../utils/userStorage";
import { useSnackbar } from "notistack";
import { DotLoader } from "../../components/dotLoader";
import { useI18n } from "../../locales";
import { getEventTypeKey, getEventTypeValue } from "../../utils/langUtils";

export const MAPTYPE = [
  { name: "일반", value: 1 },
  { name: "위성", value: 3 },
];

const { /*sm, md,*/ lg } = theme.screen;

type Prop = {
  isLoading: boolean;
  registeredAt: string;
  mapLevel: number;
  setMapLevel: React.Dispatch<React.SetStateAction<number>>;
  eventName: string;
  latitude: number;
  longitude: number;
};
export const MarkerMap = ({
  isLoading,
  registeredAt,
  mapLevel,
  setMapLevel,
  eventName,
  latitude,
  longitude
}: Prop) => {
  const { t } = useI18n();
  const { enqueueSnackbar } = useSnackbar();
  //const user = userStorage.get();
  // const [map, setMap] = useState<kakao.maps.Map>();
  const [mapTypeId, setMapTypeId] = useState(1);
  const [markerFilterList, setMarkerFilterList] = useState<string[]>([]);
  const [markerData, setMarkerData] = useState<RouteInfo>();
  const [eventAddress, setEventAddress] = useState("");
  const [eventAddressIsLoading, setEventAddressIsLoading] =
    useState(false);
  const [center, setCenter] = useState({
    lat: Number(latitude),
    lng: Number(longitude),
  });
  
  // 맵 제공자를 state로 관리하고 이전 center 값을 저장하여 무한 루프 방지
  const currentMapProvider = useMapProvider(center.lat, center.lng, mapLevel).mapProvider;
  const [mapProvider, setMapProvider] = useState(currentMapProvider);
  const [lastCenter, setLastCenter] = useState(center);
  
  // mapProvider가 실제로 변경될 때만 업데이트 (center가 충분히 이동했을 때만)
  useEffect(() => {
    // center가 의미있게 변경되었는지 확인 (소수점 3자리, 약 100m)
    const latDiff = Math.abs(center.lat - lastCenter.lat);
    const lngDiff = Math.abs(center.lng - lastCenter.lng);
    const hasMovedSignificantly = latDiff > 0.001 || lngDiff > 0.001;
    
    if (currentMapProvider !== mapProvider && hasMovedSignificantly) {
      console.log(`🔄 지도 제공자 변경: ${mapProvider} → ${currentMapProvider}`);
      
      // 지도 제공자 즉시 변경 (지도는 바로 표시)
      setMapProvider(currentMapProvider);
      setLastCenter(center);
    }
  }, [currentMapProvider, mapProvider, center, lastCenter]);
  //const [useFillter/*, setUseFillter*/] = useState(false);
  //const [useOverlay/*, setUseOverlay*/] = useState(false);

  // 지도에서 마커 필터 아이콘 클릭
  const handleClickMarkerFilter = (filter: string) => {
    // 필터 배열에 선택된 필터가 없는 경우
    if (!markerFilterList.includes(filter)) {
      setMarkerFilterList((prev) => [...prev, filter]);
    }
    // 필터 배열에 선택된 필터가 있는 경우
    else if (markerFilterList.includes(filter)) {
      setMarkerFilterList((prev) => prev.filter((el) => el !== filter));
    }
  };

  // 지도 타입 토글 버튼 클릭
  const handleClickMapType = (el: { name: string; value: number }) => {
    setMapTypeId(el.value);
  };

  // 이벤트 발생 지점의 주소 구하기
  useEffect(() => {
    setEventAddressIsLoading(true);

    const getRoad = async () => {
      try {
        const address = await getAddressFromCoords(latitude, longitude);
        setEventAddress(address);
      } catch (error) {
        console.error("주소 조회 오류:", error);
        setEventAddress("주소 조회 실패");
      } finally {
        setEventAddressIsLoading(false);
      }
    };

    getRoad();
  }, [latitude, longitude]);

  // 지도 중심 좌표 이동
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;

    //setMapLevel(1);
    timeoutId = setTimeout(() => {
      setCenter(prevCenter => ({
        lat: prevCenter.lat,
        lng: prevCenter.lng,
      }));
    }, 500);

    return () => timeoutId && clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    // 마커필터 초기값 설정
    const eventTypeList = [
      "배터리부족",
      "사용지역이탈",
      "위험운전",
      "차량전복",
      "시동방치",
    ];
    setMarkerFilterList([]);
    if (eventName && eventTypeList.includes(eventName)) {
      setMarkerFilterList([eventName]); 
    }

      return;
  }, [eventName]);

  // 마커필터 상태에 따라 지도에 출력될 마커 리스트 관리
  useEffect(() => {
    setMarkerData(
      {
        registered_at: registeredAt,
        latitude: latitude,
        longitude: longitude,
        event_type: eventName as EventType,
      }
    );
    return;
  }, [markerFilterList, registeredAt, latitude, longitude, eventName]);

  // 이벤트명을 언어팩에 맞게 변환 (배터리 전압 등 추가 정보는 유지)
  const getLocalizedEventLabel = (rawEvent: string) => {
    const eventKey = getEventTypeKey(rawEvent, t);
    if (eventKey && eventKey !== "normal") {
      const baseLabel = getEventTypeValue(eventKey, t);
      const voltageMatch = rawEvent.match(/\([^)]*\)/);
      const bracketMatch = rawEvent.match(/\[[^\]]*\]/);
      const additionalInfo = voltageMatch
        ? voltageMatch[0]
        : bracketMatch
        ? bracketMatch[0]
        : "";
      return additionalInfo ? `${baseLabel}${additionalInfo}` : baseLabel;
    }

    return rawEvent;
  };

  return (
    // <HeadWrapperStyledDiv>
    //     <SubTitleStyledH2>
    //       최근 운행내역
    //     </SubTitleStyledH2>
    //     <div>
    //     </div>
    //   </HeadWrapperStyledDiv>
          <WrapperStyledDiv style={{ position: "relative" }}>
            {/* 지도 로딩스피너 */}
            {isLoading && (
              <SpinnerContainerStyledDiv>
                {
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                    }}
                  >
                    <SpinnerForMap />
                    <p
                      style={{
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "x-large",
                        textShadow: "0px 0px 7px rgba(0,0,0,0.9)",
                      }}
                    >
                      조회 중입니다.
                    </p>
                  </div>
                }
              </SpinnerContainerStyledDiv>
            )}


            {/* 마커 필터 버튼 - 제거됨 */}
            {false && (
              <ButtonsContainerStyledDiv>
                {/* 배터리부족 */}
                <MarkerFilterStyledButton
                  isBlock={
                    eventName === "배터리부족"
                  }
                  isActive={markerFilterList.includes("배터리부족")}
                  onClick={() => handleClickMarkerFilter("배터리부족")}
                >
                  <WarningBatteryMarker width={"45px"} height={"45px"} />
                  <span>배터리부족</span>
                </MarkerFilterStyledButton>

                {/* 사용지역이탈 */}
                <MarkerFilterStyledButton
                  isBlock={
                    eventName === "사용지역이탈"
                  }
                  isActive={markerFilterList.includes("사용지역이탈")}
                  onClick={() => handleClickMarkerFilter("사용지역이탈")}
                >
                  <WarningEscapeMarker width={"45px"} height={"45px"} />
                  <span>사용지역이탈</span>
                </MarkerFilterStyledButton>

                {/* 시동방치 */}
                <MarkerFilterStyledButton
                  isBlock={
                    eventName === "시동방치"
                  }
                  isActive={markerFilterList.includes("시동방치")}
                  onClick={() => handleClickMarkerFilter("시동방치")}
                >
                  <IdleTimeMarker width={"45px"} height={"45px"} />
                  <span>시동방치</span>
                </MarkerFilterStyledButton>

                {/* 위험운전 */}
                <MarkerFilterStyledButton
                  isBlock={
                    eventName === "위험운전"
                  }
                  isActive={markerFilterList.includes("위험운전")}
                  onClick={() => handleClickMarkerFilter("위험운전")}
                >
                  <DangerousDrivingMarker width={"45px"} height={"45px"} />
                  <span>위험운전</span>
                </MarkerFilterStyledButton>

                {/* 차량전복 */}
                <MarkerFilterStyledButton
                  isBlock={
                    eventName === "차량전복"
                  }
                  isActive={markerFilterList.includes("차량전복")}
                  onClick={() => handleClickMarkerFilter("차량전복")}
                >
                  <AccidentMarker width={"45px"} height={"45px"} />
                  <span>차량전복</span>
                </MarkerFilterStyledButton>
              </ButtonsContainerStyledDiv>
            )}

            {/* 지도 타입 전환 버튼 (지도 위에 오버레이) */}
            <MapTypeToggleWrapperStyledDiv>
              {/* 일반 | 위성 버튼 */}
              {MAPTYPE.map((el, idx) => (
                <MapTypeBoxStyledDiv
                  key={idx.toString()}
                  onClick={() => handleClickMapType(el)}
                  isSelected={el.value === mapTypeId}
                  isFirst={idx === 0}
                >
                  {el.name}
                </MapTypeBoxStyledDiv>
              ))}
            </MapTypeToggleWrapperStyledDiv>

            {/* 지도 */}
            <UnifiedMap
              minLevel={0}
              // onCreate={(map) => setMap(map)}
              center={{
                lat: Number(center.lat),
                lng: Number(center.lng),
              }}
              style={{ width: "100%", height: "100%" }}
              level={mapLevel}
              mapTypeId={mapTypeId}
              onIdle={(target) => {
                // center가 의미있게 변경되었을 때만 업데이트 (무한 루프 방지)
                const newLat = target.getCenter().getLat();
                const newLng = target.getCenter().getLng();
                const latDiff = Math.abs(newLat - center.lat);
                const lngDiff = Math.abs(newLng - center.lng);
                
                if (latDiff > 0.0001 || lngDiff > 0.0001) {
                  setCenter({
                    lat: newLat,
                    lng: newLng,
                  });
                }
                setMapLevel(target.getLevel());
              }}
            >

              {/* 경로 표시 부분 
              {!routeDataIsLoading &&
                routeData?.length === 0 &&
                selectedReport &&
                selectedReport.start_latitude &&
                selectedReport.end_latitude && (
                  <Polyline
                    path={[
                      {
                        lat: selectedReport.start_latitude,
                        lng: selectedReport.start_longitude,
                      },
                      {
                        lat: selectedReport.end_latitude,
                        lng: selectedReport.end_longitude,
                      },
                    ]}
                    strokeWeight={14} // 선의 두께 입니다
                    strokeColor={
                      !selectedReport.events
                        ? `${theme.color.green_500}`
                        : selectedReport.events.some((el) =>
                            [
                              "차량전복",
                              "사용지역이탈",
                              "배터리부족",
                            ].includes(el)
                          )
                        ? `${theme.color.red_500}`
                        : `${theme.color.orange_500}`
                    }
                    strokeOpacity={0.7} // 선의 불투명도 입니다 1에서 0 사이의 값이며 0에 가까울수록 투명합니다
                    strokeStyle={"solid"} // 선의 스타일입니다
                  />
                )}
              {!routeDataIsLoading && routeData && (
                <>
                  <Polyline
                    path={routeData?.map((el) => {
                      return { lat: el.latitude, lng: el.longitude };
                    })}
                    strokeWeight={14} // 선의 두께 입니다
                    strokeColor={
                      routeData.some((el) =>
                        ["차량전복", "사용지역이탈", "배터리부족"].includes(
                          el.event_type
                        )
                      )
                        ? `${theme.color.red_500}`
                        : routeData.some((el) =>
                            ["위험지역진입"].includes(el.event_type)
                          )
                        ? `${theme.color.orange_500}`
                        : `${theme.color.green_500}`
                    }
                    strokeOpacity={0.7} // 선의 불투명도 입니다 1에서 0 사이의 값이며 0에 가까울수록 투명합니다
                    strokeStyle={"solid"} // 선의 스타일입니다
                  />

                  {CustomMarkers(routeData, selectedReport, reportData)}
                </>
              )}
              */}

              {/* 마커 부분 */}
              {
                // 단일 이벤트 마커 및 오버레이
                markerData && markerData.event_type !== "정상" && (
                  (() => {
                    const eventKey = getEventTypeKey(
                      markerData.event_type,
                      t
                    );
                    const isRollover =
                      eventKey === "vehicleRolloverEvent" ||
                      markerData.event_type === "차량전복";
                    const label = getLocalizedEventLabel(
                      markerData.event_type
                    );

                    return (
                      <div>
                        <UnifiedMarker
                          zIndex={
                            isRollover
                              ? theme.zindex.redMarker
                              : theme.zindex.orangeMarker
                          }
                          clickable={false}
                          position={{
                            lat: markerData.latitude,
                            lng: markerData.longitude,
                          }}
                          image={{
                            src: isRollover ? redmarker : orangemarker,
                            size: {
                              width: 40,
                              height: 40,
                            },
                            options: {
                              offset: {
                                x: 20,
                                y: 20,
                              },
                            },
                          }}
                        />
                        {
                          /*useOverlay &&*/ (
                            <UnifiedOverlay
                              clickable={true}
                              interactive={true}
                              position={{
                                lat: markerData.latitude,
                                lng: markerData.longitude,
                              }}
                              yAnchor={1.5}
                              xAnchor={0.5}
                              zIndex={theme.zindex.overlayMap}
                            >
                              <OverlayInnerStyledDiv>
                                <OverlayStatusStyledP status={eventKey}>
                                  {label}(
                                  {formatDate(
                                    markerData.registered_at,
                                    "HH:mm"
                                  )}
                                  )
                                </OverlayStatusStyledP>
                                <OverlayAddressStyledDiv>
                                  {eventAddressIsLoading && <DotLoader />}
                                  {!eventAddressIsLoading && eventAddress}
                                </OverlayAddressStyledDiv>

                                <CopyContainerStyledDiv>
                                  <div
                                    style={{
                                      display: "flex",
                                      marginRight: "10px",
                                    }}
                                    onClick={() => {
                                      copyToClipboard(eventAddress);
                                      enqueueSnackbar(
                                        `클립보드에 복사되었습니다.`,
                                        {
                                          variant: "success",
                                        }
                                      );
                                    }}
                                  >
                                    <p>복사</p>
                                    <CopyButton />
                                  </div>
                                </CopyContainerStyledDiv>

                                {
                                  <BubbleStyledDiv>
                                    <BubbleTailIcon height="25px" />
                                  </BubbleStyledDiv>
                                }
                              </OverlayInnerStyledDiv>
                            </UnifiedOverlay>
                          )
                        }
                      </div>
                    );
                  })()
                )
              }
            </UnifiedMap>
          </WrapperStyledDiv>

    
  );
};

const WrapperStyledDiv = styled.div`
background: ${theme.color.neutral_50};
border: 3px solid ${theme.color.neutral_100};
border-radius: 15px;
height: 583px;
width: 583px;
display: flex;
flex-direction: column;
overflow: hidden;

@media (max-width: ${lg}px) {
  height: 600px;
}
@media print {
  max-height: 583px;
}
`;

const SpinnerContainerStyledDiv = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 15;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(231, 229, 228, 0.6);
`;

const ButtonsContainerStyledDiv = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  height: 70px;
  gap: 5px;
`;

const MarkerFilterStyledButton = styled.button<{
  isActive?: boolean;
  isBlock?: boolean;
}>`
  display: ${({ isBlock }) => (isBlock ? "flex" : "none")};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0;
  background-color: transparent;
  border: none;
  transition: 200ms;
  filter: ${({ isActive }) =>
    isActive ? "grayscale(0)" : "grayscale(1)"};

  svg {
    cursor: pointer;
  }
  span {
    background-color: ${({ isActive }) =>
      isActive ? `${theme.color.red_100}` : `${theme.color.neutral_500}`};
    color: ${({ isActive }) =>
      isActive ? `${theme.color.neutral_500}` : `${theme.color.red_100}`};
    font-weight: ${({ isActive }) => isActive && "bold"};
    font-size: 11px;
    border-radius: 10px;
    cursor: pointer;
    padding-left: 5px;
    padding-right: 5px;
  }
  &:hover {
    scale: 110%;
  }
`;

const MapTypeToggleWrapperStyledDiv = styled.div`
  position: absolute;
  top: 20px;
  right: 30px;
  z-index: 50;
  cursor: pointer;
`;

const MapTypeBoxStyledDiv = styled.div<{
  isFirst: boolean;
  isSelected: boolean;
}>`
  width: 39px;
  height: 44px;
  // isFirst 왜 들어간거지 ?
  border-top-left-radius: ${({ isFirst }) => (isFirst ? "73px" : "0px")};
  border-top-right-radius: ${({ isFirst }) => (isFirst ? "73px" : "0px")};
  border-bottom-left-radius: ${({ isFirst }) =>
    isFirst ? "0px" : "73px"};
  border-bottom-right-radius: ${({ isFirst }) =>
    isFirst ? "0px" : "73px"};
  border-bottom: ${({ isFirst }) =>
    isFirst ? `1px solid ${theme.color.neutral_200}` : ""};
  background: ${theme.color.white};
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 16px;
  font-weight: ${({ isSelected }) => (isSelected ? 700 : 400)};
  font-size: 13px;
  line-height: 15px;
  color: ${({ isSelected }) =>
    isSelected
      ? `${theme.color.neutral_800}`
      : `${theme.color.neutral_300}`};
  text-align: center;

  &:hover {
    background-color: ${theme.color.neutral_100};
  }
`;

const OverlayInnerStyledDiv = styled.div`
  width: 212px;
  padding-top: 18px;
  padding-bottom: 25px;
  padding-left: 15px;
  padding-right: 15px;
  background: ${theme.color.white};
  border-radius: 20px;
  cursor: default;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
`;

const OverlayStatusStyledP = styled.p<{ status: string }>`
  font-weight: 700;
  font-size: 14px;
  text-align: center;
  color: ${({ status }) =>
    status === "vehicleRolloverEvent"
      ? `${theme.color.red_500}`
      : `${theme.color.orange_500}`};
`;

const OverlayAddressStyledDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 120%;
  text-align: center;
  min-height: 36px;
  color: ${theme.color.neutral_800};
  white-space: pre-line;
`;

const CopyContainerStyledDiv = styled.div`
  position: absolute;
  bottom: 5px;
  right: 10px;
  color: ${theme.statusColor.MAIN};
  display: flex;
  justify-content: flex-end;
  width: 100%;
  p {
    font-size: 11px;
    text-align: center;
    line-height: 20px;
  }

  div {
    padding: 5px;
    cursor: pointer;
    &:hover {
      background-color: ${theme.color.neutral_100};
      border-radius: 5px;
    }
  }
`;

const BubbleStyledDiv = styled.div`
  position: absolute;
  bottom: -25px;
  right: 50%;
  transform: translateX(50%);
  z-index: 10;

  img {
    width: 35px;
  }
`;

// const SubTitleStyledH2 = styled.h2`
//   display: flex;
//   align-items: center;
//   justify-content: space-between;
//   font-weight: 700;
//   font-size: 15px;
//   line-height: 18px;
//   color: ${theme.color.neutral_600};
//   padding-left: 20px;
//   padding-right: 20px;
//   border-bottom: 3px solid ${theme.color.neutral_200};
//   padding-bottom: 20px;
// `;

// const HeadWrapperStyledDiv = styled.div`
//   background: ${theme.color.neutral_50};
//   border: 3px solid ${theme.color.neutral_100};
//   border-radius: 15px;
//   height: 583px;
//   padding-top: 20px;
//   display: flex;
//   flex-direction: column;
  
//   overflow: hidden;

//   @media (max-width: ${lg}px) {
//     height: 600px;
//   }
//   @media print {
//     max-height: 583px;
//   }
// `;
